/**
 * Compares two objects and generates a diff result object.
 *
 * @param {Object} oldVal - The old object.
 * @param {Object} newVal - The new object.
 * @returns {Object} - The diff result object.
 */
export default function(oldVal, newVal) {
  let resultObj = {};

  function _innerLoop(oldVal, newVal, result = resultObj) {
    const oldKeys = Object.keys(oldVal);
    const newKeys = Object.keys(newVal);

    let allKeys = [...oldKeys, ...newKeys];
    let uniqueKeys = unique(allKeys);

    for (const key of uniqueKeys) {
      //console.group(`Comparing value at key: ${key}`);

      const newV = newVal[key] ?? null;
      const oldV = oldVal[key] ?? null;

      //console.log(`new: ${newV}, old: ${oldV}`);

      if (isNone(oldV) && isNone(newV)) {
        result[key] = {newValue: newV, previous: oldV, action: 'none'};
        //console.log('Both values are none');
        //console.groupEnd();
        continue;
      }

      if (isScalarType(newV)) {
        result[key] = {};
        result[key].newValue = newV;
        if (isNone(oldV)) {
          //console.log('New value is scalar, Old value is none');
          result[key].action = 'added';
          result[key].previous = null;
        } else if (isScalarType(oldV)) {
          if (newV === oldV) {
            //console.log('Both values are scalar and identical');
            result[key].action = 'none';
          } else {
            //console.log('Both values are scalar and different');
            result[key].action = 'updated';
          }

          //console.groupEnd();
          result[key].previous = oldV;
          continue;
        }
      }else if (isNone(newV) && isScalarType(oldV)) {
        result[key] = {};
        result[key].previous = oldV;
        result[key].action = 'deleted';
      }


      if (isList(newV)) {
        //console.log('New value is a list, traversing it...');
        result[key] = {value: []};
        _innerLoop(oldV ?? {}, newV, result[key].value);
        //console.groupEnd();
        continue;
      }

      if (isSet(newV)) {
        //console.log('New value is a set, traversing it...');
        result[key] = {value: {}};
        _innerLoop(oldV ?? {}, newV, result[key].value);
      }
      //console.groupEnd();
    }
  }
  _innerLoop(oldVal, newVal);
  return resultObj;
}

function isScalarType(value) {
  return isString(value) || isDateString(value) || isNumber(value) || isBoolean(value);
}

function isString(value) {
  return typeof value === 'string' && value.length > 0 && !isDateString(value);
}

function isDateString(value) {
  return typeof value === 'string' && value.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
}

function isNumber(value) {
  return typeof value === 'number';
}

function isBoolean(value) {
  return typeof value === 'boolean';
}

function isNone(value) {
  return value === null || value === undefined || value.length === 0;
}

function isSet(value) {
  return typeof value === 'object' && !isList(value) && !isNone(value);
}

function isList(value) {
  return Array.isArray(value);
}

/**
 * @param {string[]} arr
 * @return {string[]}
 */
function unique(arr) {
  let remember = [];
  for (let i = 0; i < arr.length; i++) {
    const value = arr[i];
    if (!remember.includes(value)) {
      remember.push(value);
    }
  }

  return remember;
}
